import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCrypto } from '../contexts/CryptoContext';
import { supabase } from '../services/supabaseClient';
import { encryptData, decryptData } from '../services/cryptoService';
import { createVaultItem } from '../services/vaultService';
import AccountCategoryPicker, { CATEGORIES as PICKER_CATEGORIES } from '../components/AccountCategoryPicker';
import ConfirmModal from '../components/ConfirmModal';

const S = '#00F5A0';
const G = '#8892A4';

const CARD = {
  background: 'rgba(255,255,255,0.04)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 16,
};

const CATEGORY_COLORS = {
  admin: '#94a3b8', finance: '#f59e0b', pro: '#3b82f6', security: '#10b981',
  storage: '#06b6d4', communication: '#8b5cf6', social: '#ec4899',
  ecommerce: '#f97316', gaming: '#a855f7', misc: '#6b7280',
  email: '#10b981', banking: '#f59e0b', shopping: '#f97316',
  streaming: '#8b5cf6', crypto: '#06b6d4', other: '#6b7280',
};
const getCategoryMeta = (catId) => {
  const found = PICKER_CATEGORIES.find(c => c.id === catId);
  return { label: found?.label || catId, color: CATEGORY_COLORS[catId] || '#6b7280' };
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 14,
  outline: 'none',
};

const getPasswordStrength = (pwd) => {
  if (!pwd) return null;
  let score = 0;
  if (pwd.length >= 8)  score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[a-z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { level: 1, label: 'Faible',    color: '#ef4444' };
  if (score <= 3) return { level: 2, label: 'Moyen',     color: '#f97316' };
  if (score === 4) return { level: 3, label: 'Fort',      color: '#eab308' };
  return               { level: 4, label: 'Très fort', color: '#00F5A0' };
};

const VaultPage = () => {
  const [vaultItems, setVaultItems]         = useState([]);
  const [filteredItems, setFilteredItems]   = useState([]);
  const [searchTerm, setSearchTerm]         = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddModal, setShowAddModal]     = useState(false);
  const [showPassword, setShowPassword]     = useState({});
  const [copiedField, setCopiedField]       = useState({ id: null, field: null, countdown: null });
  const [editItem, setEditItem]             = useState(null);
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [error, setError]                   = useState('');
  const [deleteTarget, setDeleteTarget]     = useState(null);
  const [decryptFailedCount, setDecryptFailedCount] = useState(0);
  const clipboardTimers = useRef({});

  const { isAuthenticated, user } = useAuth();
  const { masterKey, isUnlocked } = useCrypto();
  const navigate = useNavigate();

  const emptyForm = { service_name: '', username: '', password: '', category: 'misc', subType: null, url: '', notes: '', label: '' };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (isUnlocked) loadVaultItems();
    else setLoading(false);
  }, [isAuthenticated, isUnlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let filtered = vaultItems;
    if (selectedCategory !== 'all') filtered = filtered.filter(item => item.category === selectedCategory);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(item => item.service_name.toLowerCase().includes(q));
    }
    setFilteredItems(filtered);
  }, [vaultItems, searchTerm, selectedCategory]);

  const loadVaultItems = async () => {
    setLoading(true);
    setError('');
    setDecryptFailedCount(0);

    const { data, error: fetchError } = await supabase
      .from('vault_items')
      .select('id, category, encrypted_data, iv, created_at, updated_at')
      .order('created_at', { ascending: false });

    if (fetchError) {
      setError('Impossible de charger le coffre.');
      setLoading(false);
      return;
    }

    const decrypted = [];
    let failedCount = 0;
    for (const item of data) {
      try {
        const aad = `safeheir-vault-item-v1|${item.id}`;
        const plain = await decryptData(item.encrypted_data, item.iv, masterKey, aad);
        decrypted.push({
          id:           item.id,
          category:     item.category,
          created_at:   item.created_at,
          updated_at:   item.updated_at,
          service_name: plain.title,
          username:     plain.identifier,
          password:     plain.password,
          url:          plain.url   || '',
          notes:        plain.notes || '',
          label:        plain.label || '',
        });
      } catch {
        failedCount++;
      }
    }

    setVaultItems(decrypted);
    setDecryptFailedCount(failedCount);
    setLoading(false);
  };

  const resetForm  = () => { setFormData(emptyForm); setEditItem(null); setError(''); };
  const openAddModal = () => { resetForm(); setShowAddModal(true); };
  const openEditModal = (item) => {
    setFormData({ service_name: item.service_name, username: item.username, password: item.password || '', category: item.category, subType: null, url: item.url || '', notes: item.notes || '', label: item.label || '' });
    setEditItem(item); setError(''); setShowAddModal(true);
  };
  const closeModal = () => { resetForm(); setShowAddModal(false); };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    if (!formData.service_name.trim() || !formData.username.trim()) { setError('Nom du service et identifiant sont obligatoires.'); return; }
    if (!editItem && !formData.password) { setError('Le mot de passe est obligatoire.'); return; }

    setSaving(true);
    setError('');

    try {
      const payload = {
        title:      formData.service_name.trim(),
        identifier: formData.username.trim(),
        password:   formData.password || editItem?.password || '',
        url:        formData.url   || '',
        notes:      formData.notes || '',
        label:      formData.label || '',
      };

      if (editItem) {
        const aad = `safeheir-vault-item-v1|${editItem.id}`;
        const { ciphertext, iv } = await encryptData(payload, masterKey, aad);
        const { error: updateError } = await supabase
          .from('vault_items')
          .update({ encrypted_data: ciphertext, iv, category: formData.category, updated_at: new Date().toISOString() })
          .eq('id', editItem.id);

        if (updateError) { setError('Impossible de mettre à jour l\'entrée.'); return; }

        setVaultItems(vaultItems.map(i => i.id === editItem.id
          ? { ...i, service_name: payload.title, username: payload.identifier, password: payload.password, url: payload.url, notes: payload.notes, label: payload.label, category: formData.category }
          : i
        ));
        closeModal();

      } else {
        const r = await createVaultItem({ payload, category: formData.category, masterKey, userId: user.id });

        if (!r.success) { setError('Impossible de créer l\'entrée.'); return; }

        setVaultItems([{
          id:           r.id,
          category:     formData.category,
          created_at:   r.created_at,
          updated_at:   r.created_at,
          service_name: payload.title,
          username:     payload.identifier,
          password:     payload.password,
          url:          payload.url,
          notes:        payload.notes,
          label:        payload.label,
        }, ...vaultItems]);
        closeModal();
      }
    } catch (err) {
      setError('Erreur de chiffrement : ' + (err.message || 'erreur inconnue'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = (id) => {
    const item = vaultItems.find(i => i.id === id);
    setDeleteTarget(item || { id });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { error: deleteError } = await supabase
      .from('vault_items')
      .delete()
      .eq('id', deleteTarget.id);
    if (deleteError) setError('Impossible de supprimer cette entrée.');
    else setVaultItems(vaultItems.filter(item => item.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  const togglePasswordVisibility = (id) => setShowPassword(prev => ({ ...prev, [id]: !prev[id] }));

  const copyToClipboard = (text, itemId, field) => {
    const CLEAR_DELAY = 30000;
    const key = `${itemId}-${field}`;

    const doSet = () => {
      if (clipboardTimers.current[key]) clearTimeout(clipboardTimers.current[key]);

      let remaining = 30;
      setCopiedField({ id: itemId, field, countdown: remaining });

      const tick = setInterval(() => {
        remaining -= 1;
        setCopiedField(prev =>
          prev.id === itemId && prev.field === field
            ? { ...prev, countdown: remaining }
            : prev
        );
        if (remaining <= 0) clearInterval(tick);
      }, 1000);

      clipboardTimers.current[key] = setTimeout(() => {
        clearInterval(tick);
        if (navigator.clipboard) navigator.clipboard.writeText('').catch(() => {});
        setCopiedField(prev => prev.id === itemId && prev.field === field ? { id: null, field: null, countdown: null } : prev);
      }, CLEAR_DELAY);
    };

    if (navigator.clipboard) navigator.clipboard.writeText(text).then(doSet).catch(doSet);
    else {
      const el = document.createElement('textarea');
      el.value = text; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); doSet();
    }
  };

  const isCopied = (itemId, field) => copiedField.id === itemId && copiedField.field === field;

  // Garde défensive — MKGuard dans App.js affiche déjà UnlockVaultModal, mais on protège le rendu interne.
  if (!isUnlocked) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A1628', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: G, fontSize: 14 }}>Coffre verrouillé — rechargez la page pour déverrouiller.</p>
      </div>
    );
  }

  if (loading && vaultItems.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A1628', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: S, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: G, fontSize: 14 }}>Chargement du coffre-fort...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0A1628', color: '#fff', paddingTop: 80, paddingBottom: 80 }}>

      {/* ── Header ── */}
      <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ maxWidth: 1024, margin: '0 auto', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Coffre-fort</h1>
            <p style={{ margin: '4px 0 0', color: G, fontSize: 14 }}>Vos identifiants, organisés et sécurisés</p>
          </div>
          <button onClick={openAddModal}
            style={{ background: S, color: '#0A1628', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            + Ajouter
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1024, margin: '0 auto', padding: '24px 40px' }}>

        {/* ── Search ── */}
        <div style={{ marginBottom: 14 }}>
          <input
            type="text"
            placeholder="Rechercher par nom de service..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ ...inputStyle, width: '100%' }}
          />
        </div>

        {/* ── Category buttons + counter ── */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24, alignItems: 'center' }}>
          <button
            onClick={() => setSelectedCategory('all')}
            style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
              border: selectedCategory === 'all' ? 'none' : '1px solid rgba(255,255,255,0.12)',
              background: selectedCategory === 'all' ? S : 'rgba(255,255,255,0.05)',
              color: selectedCategory === 'all' ? '#0A1628' : G,
              fontWeight: selectedCategory === 'all' ? 700 : 400,
            }}
          >
            Toutes
          </button>
          {PICKER_CATEGORIES.filter(cat => vaultItems.some(i => i.category === cat.id)).map(cat => {
            const color = CATEGORY_COLORS[cat.id] || '#6b7280';
            const active = selectedCategory === cat.id;
            return (
              <button key={cat.id} onClick={() => setSelectedCategory(active ? 'all' : cat.id)}
                style={{
                  padding: '5px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                  border: active ? 'none' : `1px solid ${color}40`,
                  background: active ? color : `${color}18`,
                  color: active ? '#fff' : color,
                  fontWeight: active ? 700 : 400,
                }}
              >
                {cat.label}
              </button>
            );
          })}
          {vaultItems.length > 0 && (
            <span style={{ marginLeft: 'auto', fontSize: 13, color: G, whiteSpace: 'nowrap' }}>
              {filteredItems.length} affiché(s) sur {vaultItems.length}
            </span>
          )}
        </div>

        {/* ── Error ── */}
        {error && !showAddModal && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#f87171', fontSize: 14 }}>
            {error}
          </div>
        )}

        {/* ── Decrypt failures banner ── */}
        {decryptFailedCount > 0 && (
          <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#fbbf24', fontSize: 14 }}>
            {decryptFailedCount} item{decryptFailedCount > 1 ? 's' : ''} inaccessible{decryptFailedCount > 1 ? 's' : ''} — contacter le support
          </div>
        )}

        {/* ── Empty state ── */}
        {filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', background: 'rgba(0,245,160,0.02)', borderRadius: 16, border: '1px dashed rgba(0,245,160,0.2)' }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>
              {vaultItems.length === 0 ? 'Aucun identifiant enregistré' : 'Aucun résultat pour cette recherche'}
            </h3>
            <p style={{ color: G, fontSize: 14, maxWidth: 340, margin: '0 auto 24px' }}>
              {vaultItems.length === 0
                ? 'Commencez par ajouter votre premier identifiant pour le protéger.'
                : 'Essayez un autre terme ou sélectionnez une catégorie différente.'}
            </p>
            {vaultItems.length === 0 && (
              <button onClick={openAddModal}
                style={{ background: S, color: '#0A1628', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                Ajouter mon premier identifiant
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {filteredItems.map((item) => {
              const meta = getCategoryMeta(item.category);
              return (
                <div key={item.id} style={{ ...CARD, padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                    <div style={{ minWidth: 0 }}>
                      <h3 style={{ fontWeight: 600, fontSize: 16, margin: '0 0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.service_name}</h3>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: meta.color + '22', color: meta.color }}>{meta.label}</span>
                        {item.label && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'rgba(0,245,160,0.08)', color: 'rgba(0,245,160,0.6)' }}>{item.label}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                      <button onClick={() => openEditModal(item)}
                        style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 6, padding: '5px 10px', color: '#e2e8f0', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>Modifier</button>
                      <button onClick={() => handleDeleteItem(item.id)}
                        style={{ background: 'none', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 6, padding: '5px 10px', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>Suppr.</button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, color: G, marginBottom: 4 }}>Identifiant</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{ flex: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 6, padding: '8px 10px', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.username}</span>
                        <button onClick={() => copyToClipboard(item.username, item.id, 'username')}
                          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '0 10px', fontSize: 12, color: isCopied(item.id, 'username') ? S : G, cursor: 'pointer', flexShrink: 0, minWidth: 72 }}>
                          {isCopied(item.id, 'username') ? `Effacé dans ${copiedField.countdown}s` : 'Copier'}
                        </button>
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 12, color: G, marginBottom: 4 }}>Mot de passe</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{ flex: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {showPassword[item.id] ? item.password : '••••••••'}
                        </span>
                        <button onClick={() => togglePasswordVisibility(item.id)}
                          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '0 10px', fontSize: 12, color: G, cursor: 'pointer', flexShrink: 0 }}>
                          {showPassword[item.id] ? 'Cacher' : 'Voir'}
                        </button>
                        <button onClick={() => copyToClipboard(item.password, item.id, 'password')}
                          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '0 10px', fontSize: 12, color: isCopied(item.id, 'password') ? S : G, cursor: 'pointer', flexShrink: 0, minWidth: 72 }}>
                          {isCopied(item.id, 'password') ? `Effacé dans ${copiedField.countdown}s` : 'Copier'}
                        </button>
                      </div>
                    </div>

                    {item.url && (
                      <div>
                        <div style={{ fontSize: 12, color: G, marginBottom: 4 }}>URL</div>
                        {/^https?:\/\//i.test(item.url)
                          ? <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(0,245,160,0.7)', fontSize: 13, wordBreak: 'break-all' }}>{item.url}</a>
                          : <span style={{ color: G, fontSize: 13 }}>{item.url}</span>
                        }
                      </div>
                    )}

                    {item.notes && (
                      <div>
                        <div style={{ fontSize: 12, color: G, marginBottom: 4 }}>Notes</div>
                        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', margin: 0 }}>{item.notes}</p>
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 14, fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>
                    Créé le {new Date(item.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Confirm Delete Modal ── */}
      <ConfirmModal
        open={!!deleteTarget}
        title="Supprimer cet identifiant ?"
        message={`"${deleteTarget?.service_name || 'Cet élément'}" sera définitivement supprimé du coffre-fort. Cette action est irréversible.`}
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* ── Add / Edit Modal ── */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
          <div style={{ background: '#0A1628', borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {editItem ? "Modifier l'identifiant" : 'Ajouter un identifiant'}
              </h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', color: G, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <form onSubmit={handleSaveItem} autoComplete="off" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {error && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', color: '#f87171', fontSize: 13 }}>{error}</div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Nom du service *</label>
                <input id="vault-service-name" name="vault-service-name" autoComplete="off" type="text" required value={formData.service_name} onChange={(e) => setFormData({ ...formData, service_name: e.target.value })} placeholder="Ex : Gmail, Facebook…" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Identifiant / Email *</label>
                <input id="vault-identifier" name="vault-identifier" autoComplete="off" type="text" required value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="votre.email@exemple.com" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>
                  Mot de passe {editItem ? '(laisser vide pour conserver)' : '*'}
                </label>
                <input id="vault-password" name="vault-password" autoComplete="new-password" type="password" required={!editItem} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder={editItem ? '••••••••' : 'Votre mot de passe'} style={inputStyle} />
                {formData.password && (() => {
                  const s = getPasswordStrength(formData.password);
                  return (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', gap: 3, marginBottom: 4 }}>
                        {[1, 2, 3, 4].map(i => (
                          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= s.level ? s.color : 'rgba(255,255,255,0.1)', transition: 'background 0.2s' }} />
                        ))}
                      </div>
                      <span style={{ fontSize: 11, color: s.color }}>{s.label}</span>
                    </div>
                  );
                })()}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Catégorie</label>
                <AccountCategoryPicker
                  key={editItem?.id || 'new-item'}
                  category={formData.category}
                  subType={formData.subType}
                  onCategory={(id) => setFormData(prev => ({ ...prev, category: id, subType: null }))}
                  onSubType={(lbl) => setFormData(prev => ({ ...prev, subType: lbl }))}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>URL (optionnel)</label>
                <input id="vault-url" name="vault-url" autoComplete="off" type="url" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} placeholder="https://exemple.com" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Notes (optionnel)</label>
                <textarea id="vault-notes" name="vault-notes" autoComplete="off" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} placeholder="Informations supplémentaires…" style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Étiquette personnalisée (optionnel)</label>
                <input id="vault-label" name="vault-label" autoComplete="off" type="text" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} placeholder="Ex : Compte joint, Pro, Perso…" style={inputStyle} />
              </div>

              <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
                <button type="submit" disabled={saving}
                  style={{ flex: 1, background: S, color: '#0A1628', border: 'none', borderRadius: 8, padding: '11px 0', fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                  {saving ? '…' : editItem ? 'Enregistrer' : 'Ajouter'}
                </button>
                <button type="button" onClick={closeModal}
                  style={{ flex: 1, background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '11px 0', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VaultPage;
